import React, { type ReactElement, type FC, type MouseEventHandler } from 'react'
import cn from 'classnames'

import type { BaseProps } from 'types'

import Text, { TextRoles } from 'components/Text'
import Loader from 'components/Loader'

import styles from './style.scss'

export interface Props extends BaseProps {
  buttonType?: ButtonTypes
  type?: 'button' | 'submit' | 'reset'
  ariaLabel?: string
  disabled?: boolean
  title?: string
  isFullWidth?: boolean
  isLarge?: boolean
  icon?: ReactElement
  iconPosition?: 'left' | 'right'
  className?: string
  iconClassName?: string
  value?: string
  onClick?: MouseEventHandler<HTMLButtonElement>
  isLoading?: boolean
  loadBlueLoader?: boolean
}

export enum ButtonTypes {
  Primary = 'primary',
  PrimaryYellow = 'primary-yellow',
  Secondary = 'secondary',
  Text = 'text',
  Icon = 'icon-btn',
}

const Button: FC<Props> = ({
  buttonType = ButtonTypes.Primary,
  type = 'button',
  ariaLabel,
  disabled,
  title,
  onClick,
  isFullWidth,
  isLarge,
  icon,
  iconPosition = 'right',
  className,
  iconClassName,
  children,
  value,
  isLoading,
  loadBlueLoader,
}) => {
  const buttonClassNames = cn(styles[buttonType], className, {
    [styles['large']]: isLarge,
    [styles['full-width']]: isFullWidth,
  })

  const iconEle = React.isValidElement(icon)
    ? React.cloneElement(icon as ReactElement, { className: cn(styles['icon'], iconClassName) })
    : null

  return (
    <button
      type={type}
      aria-label={ariaLabel}
      disabled={disabled}
      value={value}
      className={buttonClassNames}
      onClick={onClick}
    >
      {isLoading ? (
        <Loader
          iconClassName={!loadBlueLoader ? styles['btn-loader'] : styles['btn-loader-blue']}
        />
      ) : (
        <>
          {iconPosition === 'left' && iconEle}
          {children}
          {Boolean(title) && <Text role={TextRoles.SubHeading}>{title}</Text>}
          {iconPosition === 'right' && iconEle}
        </>
      )}
    </button>
  )
}

export default Button
